import { Box, CircularProgress, Container } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSession } from 'src/contexts/SessionContext';
import { updatePlatform } from 'src/models/platform/platform';
import {
  fetchOnboarding,
  fetchOnboardingKey,
  updateOnboarding,
} from 'src/models/platform-onboarding/platform-onboarding';
import toast from 'src/utils/toast';

import { SMSSetup } from './Onboarding/components/SMSSetup/SMSSetup';

const SMSSetupPage = () => {
  const { sessionTrainer, session, refreshSessionAndUser } = useSession();
  const queryClient = useQueryClient();
  const platformId = session?.id;

  const { data: onboarding, isLoading: isLoadingOnboarding } = useQuery({
    queryKey: fetchOnboardingKey(session?.id),
    queryFn: () => (session?.id ? fetchOnboarding(session.id) : null),
    enabled: !!session?.id,
    staleTime: 0,
  });

  const updateOnboardingMutation = useMutation({
    mutationFn: (data: Partial<any>) => updateOnboarding(platformId!, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: fetchOnboardingKey(platformId),
      });
      await refreshSessionAndUser();
      toast.success('Changes saved successfully');
    },
    onError: () => {
      toast.error('Failed to save changes');
    },
  });

  const updatePlatformMutation = useMutation({
    mutationFn: (data: Partial<any>) => updatePlatform(platformId!, data),
    onSuccess: async () => {
      await refreshSessionAndUser();
      toast.success('Platform settings updated successfully');
    },
    onError: () => {
      toast.error('Failed to update platform settings');
    },
  });

  if (isLoadingOnboarding) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          pt: 8,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ pt: 4 }}>
      <SMSSetup
        platform={session}
        onboarding={onboarding}
        updateOnboarding={(data) => updateOnboardingMutation.mutateAsync(data)}
        updatePlatform={(data) => updatePlatformMutation.mutateAsync(data)}
        trainer={sessionTrainer}
      />
    </Container>
  );
};

export default SMSSetupPage;
